import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  DayOfWeek,
  GetScheduleNotificationRuleByIdResponseDTO,
  parseCalendarThreshold,
  RecordState,
  ScheduleNotificationRuleConditionExpandedSchema,
  ScheduleNotificationRuleOperator,
  TagEntity,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TimezoneMapping } from '@site-mate/sitemate-global-shared/lib/data-list/timezones';
import { GearbeltAssetType } from '@site-mate/sitemate-global-shared/lib/models/billings/gearbelt-asset-type.enum';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { TypeDeleteConfirmationDialog } from '@/components/common/type-delete-confirmation-dialog';
import CalendarBlankIcon from '@/components/icons/calendar-blank';
import CloseIcon from '@/components/icons/close';
import DeleteIcon from '@/components/icons/delete';
import GaugeIcon from '@/components/icons/gauge';
import PlusIcon from '@/components/icons/plus';
import QuestionIcon from '@/components/icons/question';
import { Input, inputVariants } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AsyncCreatableMultiSelect, AsyncMultiSelect, CreatableMultiSelect } from '@/components/ui/multi-select';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { Utils } from '@/legacy/utils';
import { cn } from '@/lib/utils';
import { getScheduleNotificationRuleQueryOptions } from '@/queries/notifications';
import { getUnitsSelectQueryOptions } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

dayjs.extend(utc);
dayjs.extend(tz);

// --- Constants ---

const SPECIAL_TIME_UNITS = [
  { id: 'days', name: 'Days', abbreviation: 'd' },
  { id: 'weeks', name: 'Weeks', abbreviation: 'w' },
  { id: 'months', name: 'Months', abbreviation: 'm' },
];

const isCalendarUnit = (unitId: string): boolean => SPECIAL_TIME_UNITS.some((u) => u.id === unitId);

const ATTRIBUTE_OPTIONS: { label: string; value: z.infer<typeof ScheduleNotificationRuleConditionExpandedSchema>['attribute'] }[] = [
  { label: 'Asset ID', value: 'assetId' },
  { label: 'Asset Type', value: 'assetType' },
  { label: 'Asset Tag', value: 'assetTagId' },
  { label: 'Asset Category', value: 'assetCategoryId' },
  { label: 'Asset Manufacturer', value: 'assetManufacturer' },
  { label: 'Asset Model', value: 'assetModel' },
  { label: 'Asset Location', value: 'assetLocationId' },
];

const OPERATOR_OPTIONS = [
  { label: 'Is', value: ScheduleNotificationRuleOperator.In },
  { label: 'Is not', value: ScheduleNotificationRuleOperator.Nin },
] as { label: string; value: z.infer<typeof ScheduleNotificationRuleConditionExpandedSchema>['operator'] }[];

const ATTRIBUTE_FORM_INPUT_TYPE_MAP: Record<
  z.infer<typeof ScheduleNotificationRuleConditionExpandedSchema>['attribute'],
  'multi-select' | 'text' | 'creatable-multi-select'
> = {
  assetId: 'multi-select',
  assetType: 'multi-select',
  assetTagId: 'multi-select',
  assetCategoryId: 'multi-select',
  assetManufacturer: 'creatable-multi-select',
  assetModel: 'creatable-multi-select',
  assetLocationId: 'multi-select',
};

const DAY_LABELS: { value: (typeof DayOfWeek)[number]; label: string; ariaLabel: string }[] = [
  { value: 'mon', label: 'M', ariaLabel: 'Monday' },
  { value: 'tue', label: 'T', ariaLabel: 'Tuesday' },
  { value: 'wed', label: 'W', ariaLabel: 'Wednesday' },
  { value: 'thu', label: 'T', ariaLabel: 'Thursday' },
  { value: 'fri', label: 'F', ariaLabel: 'Friday' },
  { value: 'sat', label: 'S', ariaLabel: 'Saturday' },
  { value: 'sun', label: 'S', ariaLabel: 'Sunday' },
];

function generateTimeOptions(): { label: string; value: string }[] {
  const options: { label: string; value: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const period = hour < 12 ? 'AM' : 'PM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const label = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
      options.push({ value, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

const timezoneOptions = Object.keys(TimezoneMapping)
  .map((name) => {
    const offset = dayjs.tz(dayjs(), name).utcOffset();
    const sign = offset >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offset);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    const label = `${name} (${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')})`;
    return { value: name, label };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

// --- Form Schema ---

const triggerFormSchema = z.object({
  value: z.number({ message: 'Value is required' }).nullable(),
  unitId: z.string().min(1, 'Unit is required'),
});

const formSchema = z
  .object({
    name: z.string().min(1, 'Please enter a name'),
    conditions: z.array(
      ScheduleNotificationRuleConditionExpandedSchema.superRefine((condition, ctx) => {
        if (Array.isArray(condition.value) && condition.value.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['value'],
          });
        } else if (!Array.isArray(condition.value) && condition.value.value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['value'],
          });
        }
      }),
    ),
    triggers: z
      .array(
        triggerFormSchema.superRefine((trigger, ctx) => {
          if (trigger.value === null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Value is required',
              path: ['value'],
            });
          }
        }),
      )
      .min(1, 'At least one trigger is required'),
    emails: z.array(z.string()).default([]),
    smsNumbers: z.array(z.string()).default([]),
    emailToLocationContact: z.boolean().default(false),
    daysOfWeek: z.array(z.enum(DayOfWeek)).min(1, 'Select at least one day'),
    time: z.string().min(1, 'Time is required'),
    timezone: z.string().min(1, 'Timezone is required'),
  })
  .refine((data) => data.emails.length > 0 || data.smsNumbers.length > 0, {
    message: 'At least one email or SMS number is required',
    path: ['emails'],
  });

type FormInput = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

// --- Component ---

interface ScheduleNotificationRuleSheetProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  ruleId?: string | null;
}

function parseTriggers(triggers: GetScheduleNotificationRuleByIdResponseDTO['scheduleNotificationRule']['triggers']) {
  return triggers.map((trigger) => {
    if ('calendarThreshold' in trigger) {
      const parsed = parseCalendarThreshold(trigger.calendarThreshold);
      if (parsed) {
        const calendarUnit = SPECIAL_TIME_UNITS.find((u) => u.abbreviation === parsed.unit);
        return { value: parsed.value, unitId: calendarUnit?.id ?? 'days' };
      }
      return { value: 0, unitId: 'days' };
    }
    return { value: trigger.unitThreshold, unitId: trigger.unitId };
  });
}

function buildDefaultValues(rule: GetScheduleNotificationRuleByIdResponseDTO['scheduleNotificationRule'] | undefined, userTimezone?: string): FormData {
  if (rule) {
    return {
      name: rule.name,
      conditions: rule.conditions ?? [],
      triggers: parseTriggers(rule.triggers),
      emails: rule.emails ?? [],
      smsNumbers: rule.smsNumbers ?? [],
      emailToLocationContact: rule.emailToLocationContact ?? false,
      daysOfWeek: rule.daysOfWeek ?? [],
      time: rule.time,
      timezone: rule.timezone,
    };
  }
  return {
    name: '',
    conditions: [],
    triggers: [{ value: null, unitId: '' }],
    emails: [],
    smsNumbers: [],
    emailToLocationContact: false,
    daysOfWeek: [],
    time: '',
    timezone: userTimezone ?? '',
  };
}

export function ScheduleNotificationRuleSheet({ open, onClose, ruleId }: ScheduleNotificationRuleSheetProps) {
  const toast = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { user } = useUserStore();

  const { data: rule, isFetching: isLoadingRule } = useQuery({
    ...getScheduleNotificationRuleQueryOptions({ ruleId }),
    enabled: open && !!ruleId,
  });

  const formInstance = useForm<FormInput, unknown, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: buildDefaultValues(rule),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, isDirty, errors },
  } = formInstance;

  const conditionsFieldArray = useFieldArray({ control, name: 'conditions' });
  const triggersFieldArray = useFieldArray({ control, name: 'triggers' });

  const conditions = watch('conditions');
  const triggers = watch('triggers');
  const activeAttributes = conditions.map(({ attribute }) => attribute);
  const filteredAttributes = useMemo(() => ATTRIBUTE_OPTIONS.filter(({ value }) => !activeAttributes.includes(value)), [activeAttributes]);

  // Fetch workspace units for trigger grouped select
  const { data: workspaceUnitsData } = useQuery({ ...getUnitsSelectQueryOptions(), enabled: open });

  type UnitOption = { id: string; name: string; abbreviation: string };

  const unitGroupedOptions = useMemo(() => {
    const workspaceUnits: UnitOption[] = (workspaceUnitsData?.data ?? []).map((unit) => ({
      id: unit.id,
      name: unit.name,
      abbreviation: unit.abbreviation || '',
    }));
    return [
      { label: 'Calendar', options: SPECIAL_TIME_UNITS },
      { label: 'Unit', options: workspaceUnits },
    ];
  }, [workspaceUnitsData]);

  const unitOptions = useMemo<UnitOption[]>(() => unitGroupedOptions.flatMap((g) => g.options), [unitGroupedOptions]);

  const attributeOptionsMap: Record<
    z.infer<typeof ScheduleNotificationRuleConditionExpandedSchema>['attribute'],
    { initialOptions?: { label: string; value: string }[]; fetchOptions?: (inputValue: string) => Promise<{ label: string; value: string }[]> }
  > = useMemo(
    () => ({
      assetId: {
        fetchOptions: async (inputValue?: string) => {
          const response = await ApiService.GetAssets({
            limit: 10,
            filters: { search: inputValue, state: RecordState.Active },
          });
          return (await response.json()).data.map(({ id, internalId }) => ({ label: internalId, value: id }));
        },
      },
      assetType: {
        initialOptions: [
          { label: 'Assets', value: GearbeltAssetType.Assets },
          { label: 'Tools and Equipment', value: GearbeltAssetType.ToolsAndEquipment },
        ],
      },
      assetTagId: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetTags({
            limit: 10,
            filters: { fields: { entity: TagEntity.Asset }, search: inputValue },
          });
          return (await response.json()).data.map(({ id, tag }) => ({ label: tag, value: id }));
        },
      },
      assetCategoryId: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetAssetCategories({
            limit: 100,
            filters: { search: inputValue },
          });
          return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
        },
      },
      assetManufacturer: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetManufacturers({
            limit: 10,
            filters: { search: inputValue },
          });
          return (await response.json()).data.map(({ make }) => ({ label: make, value: make }));
        },
      },
      assetModel: {},
      assetLocationId: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetLocations({
            limit: 10,
            filters: { search: inputValue, state: RecordState.Active },
          });
          return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
        },
      },
    }),
    [],
  );

  // Reset form when rule data loads or when opening for create
  useEffect(() => {
    if (rule && !isLoadingRule) {
      reset(buildDefaultValues(rule));
    }
    if (!ruleId) {
      reset(buildDefaultValues(undefined, user?.timezone ?? undefined));
    }
  }, [rule, isLoadingRule, ruleId, reset, user?.timezone]);

  function handleClose(refresh?: boolean) {
    reset(buildDefaultValues(undefined, user?.timezone ?? undefined));
    onClose(refresh);
  }

  async function handleDeleteRule() {
    if (!ruleId) return;
    setIsDeleting(true);
    try {
      await ApiService.DeleteScheduleNotificationRule({ scheduleNotificationRuleId: ruleId });
      toast.success('Rule deleted successfully');
      setIsDeleteDialogOpen(false);
      handleClose(true);
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    } finally {
      setIsDeleting(false);
    }
  }

  function convertConditionsToRaw(formConditions: FormData['conditions']) {
    return formConditions.map(({ value, operator, ...condition }) => {
      if (operator === ScheduleNotificationRuleOperator.In || operator === ScheduleNotificationRuleOperator.Nin) {
        return {
          ...condition,
          operator,
          value: Array.isArray(value) ? value.map((v: { value: string }) => v.value) : [],
        };
      }
      return {
        ...condition,
        operator,
        value: (value as { value: string }).value,
      };
    });
  }

  function convertTriggersToRaw(formTriggers: FormData['triggers']) {
    return formTriggers.map(({ value, unitId }) => {
      const safeValue = value ?? 0;
      if (isCalendarUnit(unitId)) {
        const abbreviation = SPECIAL_TIME_UNITS.find((u) => u.id === unitId)?.abbreviation ?? 'd';
        return { calendarThreshold: `${safeValue}${abbreviation}` };
      }
      return { unitId, unitThreshold: safeValue };
    });
  }

  async function onSubmit(data: FormData) {
    try {
      const conditions = convertConditionsToRaw(data.conditions);
      const triggers = convertTriggersToRaw(data.triggers);

      const payload = {
        name: data.name,
        conditions,
        triggers,
        emails: data.emails,
        smsNumbers: data.smsNumbers,
        emailToLocationContact: data.emailToLocationContact,
        daysOfWeek: data.daysOfWeek,
        time: data.time,
        timezone: data.timezone,
      };
      if (ruleId) {
        await ApiService.UpdateScheduleNotificationRule(ruleId, payload);
        toast.success('Rule updated successfully');
      } else {
        await ApiService.CreateScheduleNotificationRule(payload);
        toast.success('Rule created successfully');
      }
      handleClose(true);
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save rule');
    }
  }

  return (
    <Sheet open={open} onOpenChange={() => handleClose()}>
      <SheetContent className="w-full max-w-lg overflow-y-auto p-0" hideCloseIcon>
        <form onSubmit={handleSubmit(onSubmit)} className="flex h-full flex-col">
          <SheetHeader className="bg-default-white flex flex-row items-center justify-between space-y-0 border-b">
            <SheetTitle>{ruleId ? 'View' : 'Create'} Schedule Checks Notification</SheetTitle>
            <div className="flex items-center gap-1">
              {/* TODO: Uncomment help button below once we have the help article link */}
              {/* <Button type="button" variant="ghost" icon={<QuestionIcon />} aria-label="Help" /> */}
              {ruleId && (
                <Button type="button" variant="ghost" className="text-default-red" onClick={() => setIsDeleteDialogOpen(true)} aria-label="Delete rule">
                  <DeleteIcon size={20} />
                </Button>
              )}
              <Button variant="success" type="submit" disabled={!isDirty || isSubmitting} loading={isSubmitting}>
                Save
              </Button>
              <Button type="button" variant="ghost" onClick={() => handleClose()} aria-label="Close">
                <CloseIcon size={20} />
              </Button>
            </div>
          </SheetHeader>

          <div className="flex flex-col gap-4 overflow-y-auto p-4">
            {/* Rule Name */}
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Label className="text-sm">Rule Name</Label>
                  <Input {...field} placeholder="Enter rule name" error={!!error} />
                  {error && <p className="text-default-red mt-1 text-sm">{error.message}</p>}
                </div>
              )}
            />

            {/* Conditions */}
            <div className="flex flex-col gap-2">
              <Label>
                <b>IF</b> Schedule Checks match these conditions:
              </Label>
              <div className="flex flex-col gap-2">
                {conditionsFieldArray.fields.map((field, index) => (
                  <Fragment key={field.id}>
                    <div className="flex items-start gap-1">
                      <Controller
                        control={control}
                        name={`conditions.${index}.attribute`}
                        render={({ field: { value: attributeValue, onChange } }) => (
                          <div className="flex flex-1 items-start gap-1">
                            <Select<(typeof ATTRIBUTE_OPTIONS)[number]>
                              placeholder="Select..."
                              className="min-w-[150px]"
                              options={filteredAttributes}
                              value={{
                                label: ATTRIBUTE_OPTIONS.find(({ value }) => value === attributeValue)?.label || '',
                                value: attributeValue,
                              }}
                              onChange={(option) => {
                                onChange(option?.value);
                                setValue(`conditions.${index}.value`, []);
                              }}
                            />

                            <Controller
                              control={control}
                              name={`conditions.${index}.operator`}
                              render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <Select<{ label: string; value: z.infer<typeof ScheduleNotificationRuleConditionExpandedSchema>['operator'] }>
                                  placeholder="Select..."
                                  className="min-w-[85px]"
                                  options={OPERATOR_OPTIONS}
                                  error={!!error}
                                  value={{
                                    label: OPERATOR_OPTIONS.find(({ value: v }) => v === value)?.label || '',
                                    value,
                                  }}
                                  onChange={(option) => onChange(option?.value)}
                                />
                              )}
                            />

                            {(() =>
                              match(ATTRIBUTE_FORM_INPUT_TYPE_MAP[attributeValue as keyof typeof ATTRIBUTE_FORM_INPUT_TYPE_MAP])
                                .with('multi-select', () => (
                                  <Controller
                                    control={control}
                                    name={`conditions.${index}.value`}
                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                      <AsyncMultiSelect<{ label: string; value: string }>
                                        key={`${index}-${attributeValue}`}
                                        placeholder="Select..."
                                        className="w-full"
                                        defaultOptions={attributeOptionsMap[attributeValue as keyof typeof attributeOptionsMap].initialOptions || true}
                                        loadOptions={attributeOptionsMap[attributeValue].fetchOptions ?? undefined}
                                        value={value}
                                        error={!!error}
                                        onChange={(values) => onChange(values || [])}
                                        closeMenuOnSelect={false}
                                      />
                                    )}
                                  />
                                ))
                                .with('creatable-multi-select', () => (
                                  <Controller
                                    control={control}
                                    name={`conditions.${index}.value`}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                      <AsyncCreatableMultiSelect<{ label: string; value: string }>
                                        key={`${index}-${attributeValue}`}
                                        placeholder="Select..."
                                        className="w-full"
                                        defaultOptions={attributeOptionsMap[attributeValue as keyof typeof attributeOptionsMap].initialOptions || true}
                                        loadOptions={attributeOptionsMap[attributeValue].fetchOptions ?? undefined}
                                        value={value}
                                        error={!!error}
                                        onChange={(values) => onChange(values || [])}
                                        isClearable
                                        closeMenuOnSelect={false}
                                      />
                                    )}
                                  />
                                ))
                                .with('text', () => <Input placeholder="Enter..." />)
                                .exhaustive())()}
                          </div>
                        )}
                      />
                      <Button
                        variant="ghost"
                        icon={<CloseIcon />}
                        className="text-default-red w-fit"
                        type="button"
                        onClick={() => conditionsFieldArray.remove(index)}
                      />
                    </div>

                    {conditionsFieldArray.fields.length > index + 1 && <span className="text-secondary-text -my-1 text-xs font-medium">AND</span>}
                  </Fragment>
                ))}
              </div>
              {conditions.length < 3 && (
                <Button
                  variant="default"
                  className="w-fit"
                  onClick={() =>
                    conditionsFieldArray.append({
                      attribute: filteredAttributes[0]?.value ?? 'assetId',
                      operator: ScheduleNotificationRuleOperator.In,
                      value: [],
                    })
                  }
                  icon={<PlusIcon />}
                  type="button">
                  Add Condition
                </Button>
              )}
              {errors.conditions &&
                (Array.isArray(errors.conditions) ? (
                  errors.conditions.some((condition) => condition?.value) && <p className="text-default-red text-sm">Value is required</p>
                ) : (
                  <p className="text-default-red text-sm">{errors.conditions.message}</p>
                ))}

              {conditions.length === 0 && <p className="text-secondary-text text-sm">No conditions set. This rule will apply to all schedule checks.</p>}

              {conditions.length > 0 && <span className="text-secondary-text text-xs font-medium">AND</span>}

              {/* Triggers */}
              <div className="flex flex-col gap-2">
                <Label>
                  <b>IF</b> Schedule Checks match these triggers:
                </Label>
                <div className="flex flex-col gap-2">
                  {triggersFieldArray.fields.map((field, index) => (
                    <Fragment key={field.id}>
                      <div className="flex items-start gap-2">
                        <div className="flex flex-1 gap-2">
                          <div className="flex-1">
                            <Label className="text-sm">Value</Label>
                            <Controller
                              control={control}
                              name={`triggers.${index}.value`}
                              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                                <NumericFormat
                                  className={cn(inputVariants({ error: !!error }), 'w-full')}
                                  value={value ?? ''}
                                  onValueChange={(values) => onChange(values.floatValue ?? null)}
                                  onBlur={onBlur}
                                  placeholder="Type value..."
                                  allowNegative={true}
                                  decimalScale={0}
                                  thousandSeparator=","
                                />
                              )}
                            />
                          </div>

                          <div className="flex-1">
                            <Label className="text-sm">Unit</Label>
                            <Controller
                              control={control}
                              name={`triggers.${index}.unitId`}
                              render={({ field: { value, onChange }, fieldState: { error } }) => {
                                const selectedUnitIds = triggers.map((t, i) => (i !== index ? t.unitId : null)).filter((id): id is string => id !== null);

                                const availableOptions = unitGroupedOptions.map((group) => ({
                                  ...group,
                                  options: group.options.filter((opt) => !selectedUnitIds.includes(opt.id)),
                                }));

                                return (
                                  <Select<UnitOption>
                                    options={availableOptions}
                                    value={unitOptions.find((o) => o.id === value)}
                                    menuPlacement="auto"
                                    onChange={(option) => onChange(option?.id)}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    error={!!error}
                                    placeholder="Select..."
                                    formatGroupLabel={(group) => {
                                      const GroupIcon = group.label === 'Calendar' ? CalendarBlankIcon : GaugeIcon;
                                      return (
                                        <div className="text-secondary-text flex items-center gap-1 p-1 text-sm font-medium">
                                          <GroupIcon className="-mt-0.5" />
                                          <span>{group.label}</span>
                                        </div>
                                      );
                                    }}
                                    formatOptionLabel={(option, { context }) =>
                                      context === 'menu' ? (
                                        <div className="flex w-full items-center justify-between gap-2 text-sm">
                                          <span>{option.name}</span>
                                          <span className="lowercase">{option.abbreviation}</span>
                                        </div>
                                      ) : (
                                        option.name
                                      )
                                    }
                                    styles={{ menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }) }}
                                  />
                                );
                              }}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          icon={<CloseIcon />}
                          className="text-default-red mt-[25px] w-fit"
                          type="button"
                          disabled={triggersFieldArray.fields.length <= 1}
                          onClick={() => triggersFieldArray.remove(index)}
                        />
                      </div>

                      {triggersFieldArray.fields.length > index + 1 && <span className="text-secondary-text -my-1 text-xs font-medium">OR</span>}
                    </Fragment>
                  ))}
                </div>
                {triggers.length < 5 && (
                  <Button
                    variant="default"
                    className="w-fit"
                    onClick={() => triggersFieldArray.append({ value: null, unitId: '' })}
                    icon={<PlusIcon />}
                    type="button">
                    Add Trigger
                  </Button>
                )}
                {errors.triggers && !Array.isArray(errors.triggers) && <p className="text-default-red text-sm">{errors.triggers.message}</p>}
              </div>
            </div>

            {/* Recipients */}
            <div className="flex flex-col gap-2">
              <Label>
                <b>THEN</b> send a notification with a summary to these users:
              </Label>

              <Controller
                control={control}
                name="emails"
                render={({ field: { value, onChange } }) => (
                  <div>
                    <Label className="text-sm">Emails</Label>
                    <CreatableMultiSelect<{ label: string; value: string }>
                      value={value?.map((email) => ({ label: email, value: email })) || []}
                      onChange={(selected) => {
                        const emails = selected ? selected.map((item) => item.value) : [];
                        onChange(emails);
                      }}
                      onCreateOption={(inputValue) => {
                        if (Utils.ValidateEmail(inputValue) && !value?.includes(inputValue)) {
                          onChange([...(value || []), inputValue]);
                        }
                      }}
                      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                      placeholder="Type email and press enter to add..."
                      noOptionsMessage={() => 'Type email and press enter to add...'}
                      isValidNewOption={(inputValue) => Utils.ValidateEmail(inputValue) && !value?.includes(inputValue)}
                      options={[]}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="smsNumbers"
                render={({ field: { value, onChange } }) => (
                  <div>
                    <Label className="text-sm">SMS Numbers</Label>
                    <CreatableMultiSelect<{ label: string; value: string }>
                      value={value?.map((sms) => ({ label: sms, value: sms })) || []}
                      onChange={(selected) => {
                        const numbers = selected ? selected.map((item) => item.value) : [];
                        onChange(numbers);
                      }}
                      onCreateOption={(inputValue) => {
                        const normalizedNumber = Utils.normalizePhoneNumber(inputValue);
                        if (Utils.isPhoneValid(`+${normalizedNumber}`) && !value?.includes(inputValue)) {
                          onChange([...(value || []), inputValue]);
                        }
                      }}
                      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                      placeholder="Type phone number and press enter to add..."
                      noOptionsMessage={() => 'Type phone number and press enter to add..'}
                      isValidNewOption={(inputValue) => {
                        const normalizedNumber = Utils.normalizePhoneNumber(inputValue);
                        return Utils.isPhoneValid(`+${normalizedNumber}`) && !value?.includes(inputValue);
                      }}
                      options={[]}
                    />
                  </div>
                )}
              />

              {errors.emails && <p className="text-default-red text-sm">{errors.emails.message}</p>}

              <Controller
                control={control}
                name="emailToLocationContact"
                render={({ field: { value, onChange } }) => (
                  <Label className="flex items-center gap-2">
                    <Switch checked={value} onCheckedChange={onChange} />
                    <span className="text-sm">Email to location contact</span>
                  </Label>
                )}
              />
            </div>

            {/* Schedule - Days of Week */}
            <div className="flex flex-col gap-1">
              <Label>
                <b>ON</b> these days of the week:
              </Label>
              <Controller
                control={control}
                name="daysOfWeek"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <div>
                    <div className="flex gap-2">
                      {DAY_LABELS.map(({ value: dayValue, label, ariaLabel }) => {
                        const isActive = value.includes(dayValue);
                        return (
                          <button
                            aria-label={ariaLabel}
                            key={dayValue}
                            type="button"
                            onClick={() => {
                              if (isActive) {
                                onChange(value.filter((d) => d !== dayValue));
                              } else {
                                onChange([...value, dayValue]);
                              }
                            }}
                            className={cn(
                              'flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-default-blue text-default-light-blue'
                                : 'border-grey-3 bg-default-light-blue text-default-blue hover:bg-grey-1 border',
                            )}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {error && <p className="text-default-red mt-1 text-sm">{error.message}</p>}
                  </div>
                )}
              />
            </div>

            {/* Schedule - Time */}
            <div className="flex flex-col gap-2">
              <Controller
                control={control}
                name="time"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <div>
                    <Label>
                      <b>AT</b> this time:
                    </Label>
                    <Select
                      aria-label="AT this time"
                      options={TIME_OPTIONS}
                      value={TIME_OPTIONS.find((o) => o.value === value)}
                      onChange={(option) => onChange(option?.value)}
                      menuPlacement="auto"
                      error={!!error}
                    />
                    {error && <p className="text-default-red mt-1 text-sm">{error.message}</p>}
                  </div>
                )}
              />

              {/* Timezone */}
              <Controller
                control={control}
                name="timezone"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <div>
                    <Label className="text-sm">Time zone</Label>
                    <Select
                      aria-label="Time zone"
                      options={timezoneOptions}
                      value={timezoneOptions.find((option) => option.value === value)}
                      onChange={(val) => onChange(val?.value)}
                      menuPlacement="auto"
                      error={!!error}
                    />
                    {error && <p className="text-default-red mt-1 text-sm">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>
        </form>
      </SheetContent>
      <TypeDeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        callback={handleDeleteRule}
        loading={isDeleting}
        title="Delete Notification?"
        description={`Deleting will permanently delete this notification. This cannot be undone.`}
      />
    </Sheet>
  );
}
